// ButtonGroup component for radio-style button selection
"use client"

import * as React from "react"
import { cn } from "@/lib/utils"

type ButtonGroupProps = {
  value: string
  onValueChange: (value: string) => void
  className?: string
  children: React.ReactNode
}

const ButtonGroupContext = React.createContext<{
  value: string
  onValueChange: (value: string) => void
} | null>(null)

const ButtonGroup = React.forwardRef<
  HTMLDivElement,
  ButtonGroupProps
>(({ className, value, onValueChange, children, ...props }, ref) => {
  return (
    <ButtonGroupContext.Provider value={{ value, onValueChange }}>
      <div
        ref={ref}
        className={cn("inline-flex rounded-md shadow-sm", className)}
        role="group"
        {...props}
      >
        {children}
      </div>
    </ButtonGroupContext.Provider>
  )
})
ButtonGroup.displayName = "ButtonGroup"

type ButtonGroupItemProps = {
  value: string
  className?: string
  children: React.ReactNode
  disabled?: boolean
}

const ButtonGroupItem = React.forwardRef<
  HTMLButtonElement,
  ButtonGroupItemProps
>(({ className, value, children, disabled, ...props }, ref) => {
  const context = React.useContext(ButtonGroupContext)
  
  if (!context) {
    throw new Error("ButtonGroupItem must be used within a ButtonGroup")
  }
  
  const { value: selectedValue, onValueChange } = context
  const isSelected = selectedValue === value
  
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        "inline-flex items-center justify-center px-4 py-2 text-sm font-medium border border-border",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        isSelected 
          ? "bg-primary text-primary-foreground border-primary" 
          : "bg-background text-foreground hover:bg-accent hover:text-accent-foreground",
        "first:rounded-l-md last:rounded-r-md",
        "-ml-px first:ml-0",
        className
      )}
      onClick={() => onValueChange(value)}
      data-state={isSelected ? "checked" : "unchecked"}
      aria-checked={isSelected}
      role="radio"
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  )
})
ButtonGroupItem.displayName = "ButtonGroupItem"

export { ButtonGroup, ButtonGroupItem } 